import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { Rocket, Brain, Users, Lightbulb, Sparkles, Zap, Award, ChevronRight } from 'lucide-react';

const LandingPage = () => {
  const { setCurrentPage } = useAppStore();
  const [hoveredItem, setHoveredItem] = useState(null);

  const highlights = [
    {
      id: 'ssai-initiative',
      icon: Sparkles,
      title: 'SSA AI Pioneer',
      description: 'Voluntarily led the SSA AI innovation group from ground up',
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-500/20 to-pink-500/20'
    },
    {
      id: 'collaboration',
      icon: Users,
      title: 'Cross-Department Bridge',
      description: 'Connected SSA with PBU research team for innovation',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-500/20 to-cyan-500/20'
    },
    {
      id: 'innovation',
      icon: Lightbulb,
      title: 'AI Pattern Creator',
      description: 'Pioneered reusable AI patterns for practical tools',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-500/20 to-emerald-500/20'
    },
    {
      id: 'ispeed',
      icon: Zap,
      title: 'iSpeed Architect',
      description: 'Led architectural design for Taiping SG iSpeed project',
      color: 'from-orange-500 to-red-500',
      bgColor: 'from-orange-500/20 to-red-500/20'
    }
  ];

  useEffect(() => {
    console.log('LandingPage mounted at:', new Date().toISOString());
  }, []);

  return (
    <div className="page" style={{ overflowY: 'auto' }}>
      <div className="min-h-screen p-4 md:p-8 flex flex-col">
        {/* Top Section: Two Column Layout */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Column: Title + Personal Info */}
          <div className="flex-1 flex flex-col">
            {/* Title */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-4 lg:mb-6"
            >
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                2025 People Star
              </h1>
            </motion.div>

            {/* Personal Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex-1 min-h-0"
            >
              {/* Name and Title */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-4 lg:mb-6"
              >
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 lg:mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
                  Junjie Zhang
                </h2>
                <p className="text-xs sm:text-sm md:text-base text-gray-400 mb-3">
                  SSA AI Initiative Pioneer • 10 Years at Ebao
                </p>

                {/* Brief Introduction */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="max-w-2xl"
                >
                  <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                    Bridging technical excellence with team empowerment. From leading Taiping SG iSpeed architecture
                    to pioneering the SSA AI initiative, transforming vision into collective innovation.
                  </p>
                </motion.div>
              </motion.div>

              {/* Highlight Icons */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:gap-4 mt-4 lg:mt-6">
                {highlights.map((highlight, index) => {
                  const Icon = highlight.icon;
                  const isHovered = hoveredItem === highlight.id;

                  return (
                    <motion.div
                      key={highlight.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                      className="relative flex flex-col items-center"
                      onMouseEnter={() => setHoveredItem(highlight.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                    >
                      {/* Icon Container */}
                      <div className={`bg-gradient-to-br ${highlight.color} p-2 lg:p-3 rounded-lg transition-all`} style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon className="w-5 h-5 lg:w-7 lg:h-7 text-white" />
                      </div>

                      {/* Title */}
                      <div className="mt-2 text-center">
                        <h3 className={`text-[10px] sm:text-xs font-semibold px-1 ${isHovered ? 'text-white' : 'text-gray-400'}`}>
                          {highlight.title}
                        </h3>
                      </div>

                      {/* Description on Hover */}
                      <AnimatePresence>
                        {isHovered && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute z-30 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max max-w-xs"
                          >
                            <div className="bg-gray-900 border border-white/30 rounded-lg p-2 lg:p-3 shadow-xl">
                              <p className="text-[10px] lg:text-xs text-white leading-relaxed text-left">
                                {highlight.description}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Right Column: Profile Photo - Fixed height */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:w-64 xl:w-72 flex-shrink-0 self-center"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-purple-500/30" style={{ maxHeight: '50vh' }}>
              <img
                src="/pic/person.webp"
                alt="Profile"
                className="w-full h-full object-cover"
                style={{ maxHeight: '50vh' }}
                onError={(e) => {
                  e.target.src = '/pic/person.png'; // Fallback to PNG if WebP not supported
                }}
              />
            </div>
          </motion.div>
        </div>

        {/* Navigation Buttons - Bottom Section */}
        <div className="relative flex flex-col md:flex-row gap-3 md:gap-4 lg:gap-6 items-left justify-left py-3 lg:py-6">
          {/* Main Navigation Buttons */}
          <div className="flex flex-col md:flex-row gap-1 md:gap-4 items-center justify-left">
            {/* Project Path */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-pointer breathing-border rounded-xl w-full md:w-auto"
              onClick={() => setCurrentPage('project')}
            >
              <div className="relative px-4 py-3 md:px-8 md:py-6 lg:px-12 lg:py-8 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-sm border border-blue-500/30 hover:border-blue-400/50 transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl blur-xl"></div>
                <div className="relative z-10 text-center">
                  <Rocket className="w-8 h-8 md:w-12 md:h-12 lg:w-16 lg:h-16 mx-auto mb-1 md:mb-2 lg:mb-4 text-blue-400" />
                  <h3 className="text-sm md:text-xl lg:text-2xl font-bold mb-1">CN taiping Project</h3>
                  <p className="text-[10px] md:text-sm lg:text-base text-gray-300">IUL • Singpass • FAPL</p>
                </div>
              </div>
            </motion.div>

            {/* AI Path */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-pointer breathing-border rounded-xl w-full md:w-auto"
              onClick={() => setCurrentPage('ai')}
            >
              <div className="relative px-4 py-3 md:px-8 md:py-6 lg:px-12 lg:py-8 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm border border-purple-500/30 hover:border-purple-400/50 transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl blur-xl"></div>
                <div className="relative z-10 text-center">
                  <Brain className="w-8 h-8 md:w-12 md:h-12 lg:w-16 lg:h-16 mx-auto mb-1 md:mb-2 lg:mb-4 text-purple-400" />
                  <h3 className="text-sm md:text-xl lg:text-2xl font-bold mb-1">AI Pioneer</h3>
                  <p className="text-[10px] md:text-sm lg:text-base text-gray-300">RAG • CLI • Email</p>
                </div>
              </div>
            </motion.div>

            {/* Spacer for AI and Team Network gap */}
            <div className="hidden md:block md:w-8 lg:w-10"></div>
            <div className="hidden md:block md:w-8 lg:w-10"></div>

            {/* Team Network - Circular */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cursor-pointer breathing-border rounded-full w-full md:w-auto"
              onClick={() => setCurrentPage('network')}
            >
              <div className="relative w-20 h-20 md:w-48 md:h-48 lg:w-58 lg:h-58 rounded-full bg-gradient-to-br from-green-500/20 to-teal-500/20 backdrop-blur-sm border border-green-500/30 hover:border-green-400/50 transition-all duration-300 flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-full blur-xl"></div>
                <div className="relative z-10 text-center">
                  <Users className="w-8 h-8 md:w-12 md:h-12 lg:w-16 lg:h-16 mx-auto mb-1 md:mb-2 lg:mb-4 text-green-400" />
                  <h3 className="text-xs md:text-lg lg:text-xl font-bold mb-1">Team Network</h3>
                  <p className="text-[10px] md:text-xs lg:text-sm text-gray-300">Collaboration</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Summary Button - Fixed position in bottom right */}
          <div className="absolute bottom-0 right-0">
            <motion.button
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCurrentPage('summary')}
              className="breathing-border bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-sm border border-yellow-500/30 hover:border-yellow-400/50 transition-all duration-300 p-3 md:p-4 rounded-full shadow-2xl"
              title="View Complete Journey Summary"
            >
              <div className="relative z-10 flex flex-col items-center gap-1">
                <Award className="w-5 h-5 md:w-6 md:h-6 text-yellow-400" />
                <span className="text-[10px] md:text-sm font-bold text-white whitespace-nowrap">Summary</span>
              </div>
            </motion.button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes breathing {
          0%, 100% {
            box-shadow: 0 0 20px rgba(96, 165, 250, 0.3),
                        0 0 40px rgba(96, 165, 250, 0.2);
            border-color: rgba(96, 165, 250, 0.6);
          }
          50% {
            box-shadow: 0 0 30px rgba(96, 165, 250, 0.5),
                        0 0 60px rgba(96, 165, 250, 0.3);
            border-color: rgba(96, 165, 250, 0.9);
          }
        }

        .breathing-border {
          animation: breathing 3s ease-in-out infinite;
        }

        .breathing-border:hover {
          animation: none;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
